/**
 * Live transcription capture (frontend §1).
 *
 * Uses the browser's SpeechRecognition API and posts segments to the copilot
 * service, which owns the transcript. Two properties are non-negotiable and
 * shape the whole hook:
 *
 *   1. It can never disturb the call. Every failure path here ends in a state
 *      change — `unavailable` or `paused` — and nothing else. There is no throw
 *      that escapes, and no code path that touches the Zego engine.
 *
 *   2. It uses its own recognition stream, not the call's. SpeechRecognition
 *      opens its own capture from the default input device; it does not consume
 *      the published MediaStream, so publishing is unaffected.
 *
 * ── On speaker attribution ────────────────────────────────────────────────
 * The browser recognizer hears the local microphone, which is the doctor. It
 * cannot separate the patient's voice arriving over WebRTC, so every segment
 * produced here is labelled `doctor` rather than guessed at. Patient speech
 * reaches the same transcript when the patient app posts its own segments, or
 * when server-side diarisation is added — both write to the same endpoint with
 * `speaker: "patient"`, and the panel already renders them. Labelling local
 * audio "unknown" or splitting it heuristically would put words in a patient's
 * mouth, which is the one mistake a clinical transcript must not make.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { log } from "@/lib/logger";
import { copilotService, type OutgoingSegment } from "@/services/copilot/copilot.service";
import type { TranscriptState } from "@/types/copilot";

/* The Web Speech API is not in TypeScript's DOM lib; these are the parts used. */
interface SpeechAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechResult {
  readonly length: number;
  isFinal: boolean;
  item(index: number): SpeechAlternative;
  [index: number]: SpeechAlternative;
}
interface SpeechResultList {
  readonly length: number;
  item(index: number): SpeechResult;
  [index: number]: SpeechResult;
}
interface SpeechRecognitionEventLike extends Event {
  resultIndex: number;
  results: SpeechResultList;
}
interface SpeechRecognitionErrorEventLike extends Event {
  error: string;
}
interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function getRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  const scope = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

export const transcriptionSupported = () => getRecognitionConstructor() !== null;

/** Segments are batched rather than posted per utterance. */
const FLUSH_INTERVAL_MS = 2500;

export interface TranscriptionController {
  state: TranscriptState;
  supported: boolean;
  /** False outside a live call — the microphone must not be open then. */
  enabled: boolean;
  /** The utterance in progress, shown greyed at the bottom of the panel. */
  interim: string;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

export function useTranscription(
  sessionId: string | null,
  { enabled = false, onFlush }: { enabled?: boolean; onFlush?: () => void } = {},
): TranscriptionController {
  const supported = transcriptionSupported();
  const [state, setState] = useState<TranscriptState>(supported ? "idle" : "unavailable");
  const [interim, setInterim] = useState("");

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const queueRef = useRef<OutgoingSegment[]>([]);
  const sessionRef = useRef<string | null>(sessionId);
  const wantedRef = useRef(false);
  const flushingRef = useRef(false);
  const onFlushRef = useRef(onFlush);
  /**
   * Whether a call is live right now.
   *
   * Read through a ref as well as a prop because `onend` fires asynchronously
   * and restarts recognition — without this, a stream that ended just as the
   * call did would resurrect itself and hold the microphone after hang-up.
   */
  const enabledRef = useRef(enabled);
  /**
   * Set when the doctor pauses by hand.
   *
   * Transcription starts on its own, so without this the auto-start would
   * immediately undo a deliberate pause — the doctor would press Pause and watch
   * the microphone come straight back. It is cleared when the call ends, so the
   * next consultation starts transcribing again rather than inheriting a pause
   * from the last one.
   */
  const userPausedRef = useRef(false);
  /** The session auto-start has already fired for; stops it re-firing. */
  const autoStartedRef = useRef<string | null>(null);

  sessionRef.current = sessionId;
  onFlushRef.current = onFlush;
  enabledRef.current = enabled;

  /** Posts whatever has accumulated. Never throws; a failed batch is retried. */
  const flush = useCallback(async () => {
    const id = sessionRef.current;
    if (!id || flushingRef.current || queueRef.current.length === 0) return;

    flushingRef.current = true;
    const batch = queueRef.current;
    queueRef.current = [];
    try {
      await copilotService.appendTranscript(id, batch);
      onFlushRef.current?.();
    } catch (error) {
      // Put the batch back at the front so nothing said is lost to a blip.
      queueRef.current = [...batch, ...queueRef.current];
      log.warn("copilot", "transcript.flushFailed", {
        queued: queueRef.current.length,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      flushingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [flush]);

  const stop = useCallback(() => {
    // A pause is a decision, and it outranks the auto-start.
    userPausedRef.current = true;
    wantedRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch {
      /* already stopped */
    }
    recognitionRef.current = null;
    setInterim("");
    setState((current) => (current === "unavailable" ? current : "paused"));
    void flush();
  }, [flush]);

  const start = useCallback(() => {
    // The one gate that matters: no live call, no microphone. This is not a UI
    // nicety — it is the difference between the mic being open during a
    // consultation and being open while the doctor reads the prescription
    // screen afterwards.
    if (!enabledRef.current) return;
    userPausedRef.current = false;

    const Recognition = getRecognitionConstructor();
    if (!Recognition || !sessionRef.current) {
      setState("unavailable");
      return;
    }
    if (recognitionRef.current) return;

    wantedRef.current = true;
    let recognition: SpeechRecognitionLike;
    try {
      recognition = new Recognition();
    } catch {
      setState("unavailable");
      return;
    }

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";

    recognition.onresult = (event) => {
      let pending = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (!result) continue;
        const alternative = result[0];
        if (!alternative) continue;
        const text = alternative.transcript.trim();
        if (!text) continue;

        if (result.isFinal) {
          queueRef.current.push({
            text,
            speaker: "doctor",
            state: "final",
            confidence: alternative.confidence,
          });
        } else {
          pending += `${text} `;
        }
      }
      setInterim(pending.trim());
      if (queueRef.current.length >= 8) void flush();
    };

    recognition.onerror = (event) => {
      // `no-speech` and `aborted` are routine on a quiet line — they are not
      // failures and must not make the panel claim transcription is broken.
      if (event.error === "no-speech" || event.error === "aborted") return;
      log.warn("copilot", "transcription.error", { error: event.error });
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        wantedRef.current = false;
        setState("unavailable");
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      // Chrome ends the stream every so often on its own; restart while the
      // doctor still wants it running, or the transcript stops silently. The
      // `enabledRef` half is what stops that auto-restart from re-opening the
      // microphone on a stream that ended because the call did.
      if (wantedRef.current && enabledRef.current) {
        try {
          const next = new Recognition();
          next.continuous = recognition.continuous;
          next.interimResults = recognition.interimResults;
          next.lang = recognition.lang;
          next.onresult = recognition.onresult;
          next.onerror = recognition.onerror;
          next.onend = recognition.onend;
          next.start();
          recognitionRef.current = next;
          return;
        } catch {
          setState("unavailable");
        }
      }
      setInterim("");
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setState("listening");
    } catch {
      setState("unavailable");
    }
  }, [flush]);

  const toggle = useCallback(() => {
    if (state === "listening") stop();
    else start();
  }, [start, state, stop]);

  /**
   * Releases the microphone the moment the call is no longer live.
   *
   * `abort()` rather than `stop()`: stop lets the recognizer finish its current
   * utterance, which keeps the device open for a moment longer. Once the call
   * has ended there is nothing left worth transcribing, so the device goes back
   * immediately. Anything already captured is still flushed.
   */
  /**
   * Starts transcribing on its own as soon as the consultation is live.
   *
   * Fires once per session. Continuity afterwards is the `onend` handler's job —
   * Chrome ends the recognition stream every so often by itself, and that
   * restart is what makes this continuous rather than a single burst at the top
   * of the call.
   */
  useEffect(() => {
    if (!enabled || !supported || !sessionId) return;
    if (userPausedRef.current) return;
    if (autoStartedRef.current === sessionId) return;
    autoStartedRef.current = sessionId;
    log.info("copilot", "transcription.autoStarted", { sessionId });
    start();
  }, [enabled, sessionId, start, supported]);

  useEffect(() => {
    if (enabled) return;
    wantedRef.current = false;
    // A new consultation transcribes from the start rather than inheriting the
    // last one's pause or its already-started flag.
    userPausedRef.current = false;
    autoStartedRef.current = null;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        /* already gone */
      }
      recognitionRef.current = null;
      log.info("copilot", "transcription.releasedWithCall", {});
    }
    setInterim("");
    setState((current) => (current === "unavailable" ? current : "idle"));
    void flush();
  }, [enabled, flush]);

  /** Releases the microphone on unmount and posts anything still queued. */
  useEffect(
    () => () => {
      wantedRef.current = false;
      try {
        recognitionRef.current?.abort();
      } catch {
        /* nothing to release */
      }
      recognitionRef.current = null;
      void flush();
    },
    [flush],
  );

  return { state, supported, enabled, interim, start, stop, toggle };
}
